import SyncOutlined from '@mui/icons-material/SyncOutlined';
import { SvgIconProps } from '@mui/material';
import { Door, DoorBatteryWarningLevel, DoorType, Location, VisitorGroup } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import {
  getAllAccessGrantsOfVisitorGroup,
  getVisitorGroupsThatHaveLockGroupAssigned
} from 'utils/VisitorGroup/VisitorGroup';
import BatteryAndroid0Outlined from 'v2/assets/vector/lockIcons/BatteryAndroid0Outlined';
import BatteryAndroidFrame1Outlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrame1Outlined';
import BatteryAndroidFrame3Outlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrame3Outlined';
import BatteryAndroidFrameBoltOutlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrameBoltOutlined';
import BatteryAndroidFrameFullOutlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrameFullOutlined';
import BatteryAndroidFrameQuestionOutlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrameQuestionOutlined';
import CloudControllerDoor from 'v2/assets/vector/lockIcons/CloudControllerDoor';
import DOMCilinderOutlined from 'v2/assets/vector/lockIcons/DOMCilinderOutlined';
import DOMControllerOutlined from 'v2/assets/vector/lockIcons/DOMControllerOutlined';
import DOMHandleOutlined from 'v2/assets/vector/lockIcons/DOMHandleOutlined';
import DOMLocker from 'v2/assets/vector/lockIcons/DOMLocker';
import DOMReaderOutlined from 'v2/assets/vector/lockIcons/DOMReaderOutlined';
import SwingHandle from 'v2/assets/vector/lockIcons/SwingHandle';
import UnknownLock from 'v2/assets/vector/lockIcons/UnknownLock';

export const getLocksThatHaveVisitorGroupAssigned = (visitorGroups: VisitorGroup[], visitorGroupId: string) => {
  const filteredVisitorGroup = visitorGroups.find((visitorGroup) => visitorGroup.id === visitorGroupId);
  if (filteredVisitorGroup) {
    const allAccessGrantOfSelectedVisitorGroup = getAllAccessGrantsOfVisitorGroup(filteredVisitorGroup);
    return [
      ...allAccessGrantOfSelectedVisitorGroup.flatMap((accessGrant) => accessGrant.doors),
      ...allAccessGrantOfSelectedVisitorGroup.flatMap((accessGrant) =>
        accessGrant.locations.flatMap((location) => location.doors)
      )
    ];
  }
  return [];
};

export const getLockGroupsThatHaveVisitorGroupAssigned = (visitorGroups: VisitorGroup[], lockGroups: Location[]) => {
  const allLockGroupAssignedToVisitorGroup = visitorGroups?.flatMap((group) =>
    group.accessGrants.flatMap((accessGrant) => accessGrant.locations.flatMap((location) => location))
  );
  return lockGroups?.filter((lockGroup) =>
    allLockGroupAssignedToVisitorGroup?.some((assignedLockGroup) => assignedLockGroup.id === lockGroup.id)
  );
};

export const isLockGroupAssignedToVisitorGroup = (
  lockGroupId: string,
  visitorGroup: VisitorGroup,
  lockGroups: Location[]
) => {
  const lockGroupsThatHaveVisitorGroupAssigned = getLockGroupsThatHaveVisitorGroupAssigned([visitorGroup], lockGroups);
  return lockGroupsThatHaveVisitorGroupAssigned.some((assignedLockGroup) => assignedLockGroup.id === lockGroupId);
};

export const isLockAssignedToVisitorGroup = (lockId: string, visitorGroup: VisitorGroup, locks: Door[]) => {
  const doorsAssignedToVisitorGroups = getDoorsAssignedToVisitorGroups([visitorGroup], locks);
  return doorsAssignedToVisitorGroups.some((assignedDoor) => assignedDoor.id === lockId);
};

export const isLockAssignedViaLockGroupToVisitorGroup = (
  lockId: string,
  visitorGroup: VisitorGroup,
  lockGroups: Location[]
) => {
  const lockGroupsThatHaveVisitorGroupAssigned = getLockGroupsThatHaveVisitorGroupAssigned([visitorGroup], lockGroups);
  return lockGroupsThatHaveVisitorGroupAssigned.some((lockGroup) => lockGroup.doors.some((door) => door.id === lockId));
};

export const getLocksThatHaveLockGroupAssigned = (lockGroups: Location[], lockGroupId: string) => {
  return lockGroups.find((lockGroup) => lockGroup.id === lockGroupId)?.doors || [];
};

export const getFullDoorsForLockGroup = (doors: Door[], lockGroup: Location) => {
  return doors.filter((door) => lockGroup.doors.some((lockGroupDoor) => lockGroupDoor.id === door.id));
};

export const getLockGroupsThatLockBelongTo = (lockGroups: Location[], lockId: string) => {
  return lockGroups.filter((lockGroup) => lockGroup.doors.some((door) => door.id === lockId));
};

export const getDoorsAssignedToVisitorGroups = (visitorGroups: VisitorGroup[], doors: Door[]) => {
  const allDoorsAssignedToVisitor = visitorGroups.flatMap((group) => [
    ...group.accessGrants.flatMap((accessGrant) => accessGrant.doors.flatMap((door) => door.id)),
    ...group.accessGrants.flatMap((accessGrant) =>
      accessGrant.locations.flatMap((location) => location.doors.flatMap((door) => door.id))
    )
  ]);
  return doors.filter((door) => allDoorsAssignedToVisitor?.some((doorId) => doorId === door.id));
};

export enum LockType {
  Cylinder,
  Handle,
  Reader,
  Locker
}

export const getLockIcon = (door: { type?: DoorType }, iconProps?: SvgIconProps) => {
  switch (door.type) {
    case DoorType.CloudControllerDoor:
      return <CloudControllerDoor {...iconProps} />;
    case DoorType.DomCylinder:
      return <DOMCilinderOutlined {...iconProps} />;
    case DoorType.DomHandle:
      return <DOMHandleOutlined {...iconProps} />;
    case DoorType.DomReader:
      return <DOMReaderOutlined {...iconProps} />;
    case DoorType.DomLocker:
      return <DOMLocker {...iconProps} />;
    case DoorType.DomController:
      return <DOMControllerOutlined {...iconProps} />;
    case DoorType.Tanlock:
      return <SwingHandle {...iconProps} />;
  }
  return <UnknownLock {...iconProps} />;
};

export const getLockTypeName = (type: DoorType, translation: Translation) => {
  switch (type) {
    case DoorType.DomCylinder:
      return translation.cylinder;
    case DoorType.DomHandle:
      return translation.handle;
    case DoorType.DomReader:
      return translation.reader;
    case DoorType.DomLocker:
      return translation.locker;
    case DoorType.DomController:
      return translation.controller;
    case DoorType.Tanlock:
      return translation.swing_handle;
    default:
      return undefined;
  }
};

const getEmptyLockGroups = (lockGroups: Location[]) => lockGroups.filter((lockGroup) => lockGroup.doors.length === 0);

export const isLockGroupEmpty = (lockGroupId: string, lockGroups: Location[]) =>
  getEmptyLockGroups(lockGroups).some((emptyLockGroup) => emptyLockGroup.id === lockGroupId);

export const isOfficeMode = (door: Door) => door.officeMode.weekDays.some((day) => day !== null);

export const getOfficeModeTime = (door: Door): string =>
  `${door.officeMode.weekDays[0].from.slice(0, -3)} - ${door.officeMode.weekDays[0].to.slice(0, -3)}`;

export interface LockStatus {
  batteryStatus: DoorBatteryWarningLevel;
  syncRequired: boolean;
}

export const getLockStatus = (door: Door): LockStatus => ({
  batteryStatus: door.batteryWarningLevel || DoorBatteryWarningLevel.BatteryUnknown,
  syncRequired: false
});

export const getLockByLockId = (locks: Door[], lockId: string) => locks.find((lock) => lock.id === lockId);

export const getLockStatusText = (status: LockStatus, translation: Translation) => {
  if (status.syncRequired) return translation.sync_required;
  switch (status.batteryStatus) {
    case DoorBatteryWarningLevel.BatteryDefault:
      return translation.full;
    case DoorBatteryWarningLevel.BatteryLow:
      return translation.low;
    case DoorBatteryWarningLevel.BatteryCritical:
      return translation.critical;
    case DoorBatteryWarningLevel.BatteryEmpty:
      return translation.empty;
    case DoorBatteryWarningLevel.Powered:
      return translation.powered;
    case DoorBatteryWarningLevel.BatteryUnknown:
    default:
      return translation.unknown;
  }
};

export const getLockStatusColor = (
  status: LockStatus
): 'warning' | 'error' | 'default' | 'primary' | 'secondary' | 'info' | 'success' | undefined => {
  if (status.syncRequired) return 'error';
  switch (status.batteryStatus) {
    case DoorBatteryWarningLevel.BatteryDefault:
    case DoorBatteryWarningLevel.Powered:
      return 'success';
    case DoorBatteryWarningLevel.BatteryLow:
      return 'warning';
    case DoorBatteryWarningLevel.BatteryCritical:
    case DoorBatteryWarningLevel.BatteryEmpty:
      return 'error';
    case DoorBatteryWarningLevel.BatteryUnknown:
      return 'secondary';
    default:
      return undefined;
  }
};

export const getLockStatusIcon = (status: LockStatus) => {
  if (status.syncRequired) return <SyncOutlined />;
  switch (status.batteryStatus) {
    case DoorBatteryWarningLevel.BatteryDefault:
      return <BatteryAndroidFrameFullOutlined />;
    case DoorBatteryWarningLevel.BatteryLow:
      return <BatteryAndroidFrame3Outlined />;
    case DoorBatteryWarningLevel.BatteryCritical:
      return <BatteryAndroidFrame1Outlined />;
    case DoorBatteryWarningLevel.BatteryEmpty:
      return <BatteryAndroid0Outlined />;
    case DoorBatteryWarningLevel.Powered:
      return <BatteryAndroidFrameBoltOutlined />;
    case DoorBatteryWarningLevel.BatteryUnknown:
    default:
      return <BatteryAndroidFrameQuestionOutlined />;
  }
};

export const getVisitorGroupThatHaveLockAssigned = (lockId: string, visitorGroups: VisitorGroup[]) => {
  return visitorGroups.filter((visitorGroup) =>
    getAllAccessGrantsOfVisitorGroup(visitorGroup).some((accessGrant) =>
      accessGrant.doors.some((accessGrantDoor) => accessGrantDoor.id === lockId)
    )
  );
};

export const getVisitorGroupsThatHaveLocationAssigned = (locationId: string, visitorGroups: VisitorGroup[]) => {
  return visitorGroups.filter((visitorGroup) =>
    getAllAccessGrantsOfVisitorGroup(visitorGroup).some((accessGrant) =>
      accessGrant.locations.some((loc) => loc.id === locationId)
    )
  );
};

export interface DeviceGroup {
  id: string;
  name: string;
}

export interface VisitorGroupWithDeviceGroups {
  id: string;
  name: string;
  isIncomplete: boolean;
  viaDeviceGroups: DeviceGroup[];
}

// Returns user groups directly assigned to the device, plus user groups assigned only
// indirectly - because the device belongs to a device group linked to those user groups.
export const getVisitorGroupsWithAccessToDevice = (
  deviceId: string,
  deviceGroupsContainingDevice: DeviceGroup[],
  allVisitorGroups: VisitorGroup[]
): VisitorGroupWithDeviceGroups[] => {
  const directVisitorGroups = getVisitorGroupThatHaveLockAssigned(deviceId, allVisitorGroups);
  const directVisitorGroupIds = new Set(directVisitorGroups.map((visitorGroup) => visitorGroup.id));

  const indirectVisitorGroupsById = new Map<string, VisitorGroupWithDeviceGroups>();
  deviceGroupsContainingDevice.forEach((deviceGroup) => {
    getVisitorGroupsThatHaveLockGroupAssigned(allVisitorGroups, deviceGroup.id).forEach((visitorGroup) => {
      if (directVisitorGroupIds.has(visitorGroup.id)) return;

      const existingEntry = indirectVisitorGroupsById.get(visitorGroup.id);
      if (existingEntry) {
        existingEntry.viaDeviceGroups.push({ id: deviceGroup.id, name: deviceGroup.name });
      } else {
        indirectVisitorGroupsById.set(visitorGroup.id, {
          id: visitorGroup.id,
          name: visitorGroup.name,
          isIncomplete: visitorGroup.members.length === 0,
          viaDeviceGroups: [{ id: deviceGroup.id, name: deviceGroup.name }]
        });
      }
    });
  });

  return [
    ...directVisitorGroups.map((visitorGroup) => ({
      id: visitorGroup.id,
      name: visitorGroup.name,
      isIncomplete: visitorGroup.members.length === 0,
      viaDeviceGroups: []
    })),
    ...Array.from(indirectVisitorGroupsById.values())
  ];
};
